/**
 * Round-index query — the pure "filtrar + ordenar + paginar" the Tablero's
 * expediente browser runs over the round index (PRD #330, issue #331).
 *
 * The console downloads every expediente of the round once
 * (`GET /rounds/{round_id}/expediente-index`) and derives each visible page
 * locally instead of asking the server for it. The semantics mirror the paged
 * endpoint (`listRoundExpedientes`, src/vera/api/routes/review.py) exactly:
 *
 *  - `decision`: value equality on the EFFECTIVE decision, so an adjudicated
 *    APPROVED/REJECTED matches its filter; AWAITING_RESUBMISSION only shows
 *    under "ALL" (the KPI cards have no filter for it).
 *  - `reason`: the row carries that code as a failure OR as a flag.
 *  - `q` (issue #333): word-AND search over the folded `search_text`
 *    (screens/roundIndex/search.ts), applied from 2 characters on; ANDed with
 *    decision and reason.
 *  - order: actionable-first (HUMAN_REVIEW and AWAITING_RESUBMISSION, then
 *    REJECTED, then APPROVED; anything else last), then expediente id —
 *    `actionable_first_key` in src/vera/api/projections.py. The server already
 *    sends the index in this order; sorting again keeps filtering stable
 *    whatever the input order.
 */

import type { Decision, ExpedienteIndexRow } from "../../api/client";
import { compileQuery, isActiveQuery } from "./search";

export const PAGE_SIZE = 50;

/** Status filter driven by the KPI cards; "ALL" = no decision filter. */
export type DecisionFilter = "ALL" | Decision;
/** Motivo filter: a failure/flag code, or "ALL". */
export type ReasonFilter = "ALL" | string;

export type IndexQuery = {
  decision: DecisionFilter;
  reason: ReasonFilter;
  /** Free-text search (raw, as typed); ignored below 2 characters. */
  q?: string;
  /** 1-indexed; clamped into range. */
  page: number;
  pageSize?: number;
};

export type IndexPage = {
  items: ExpedienteIndexRow[];
  /** Rows matching the filters across every page. */
  total: number;
  totalPages: number;
  /** The page actually served (the requested one, clamped into range). */
  page: number;
};

const ACTIONABLE_RANK: Record<string, number> = {
  HUMAN_REVIEW: 0,
  AWAITING_RESUBMISSION: 0,
  REJECTED: 1,
  APPROVED: 2,
};

function rank(decision: string): number {
  return ACTIONABLE_RANK[decision] ?? 9;
}

/** The server's sort key: actionable-first, then expediente id. */
export function compareRows(a: ExpedienteIndexRow, b: ExpedienteIndexRow): number {
  const byRank = rank(a.decision) - rank(b.decision);
  if (byRank !== 0) return byRank;
  if (a.expediente_id < b.expediente_id) return -1;
  if (a.expediente_id > b.expediente_id) return 1;
  return 0;
}

function hasReason(row: ExpedienteIndexRow, reason: string): boolean {
  return (
    row.failures.some((f) => f.code === reason) ||
    row.flags.some((fl) => fl.code === reason)
  );
}

/** Filter by decision + reason + search, sort with the server's key, then page. */
export function filterSortPage(
  rows: readonly ExpedienteIndexRow[],
  { decision, reason, q = "", page, pageSize = PAGE_SIZE }: IndexQuery,
): IndexPage {
  const matchesQuery = isActiveQuery(q) ? compileQuery(q) : null;
  const matching = rows.filter(
    (row) =>
      (decision === "ALL" || row.decision === decision) &&
      (reason === "ALL" || hasReason(row, reason)) &&
      (matchesQuery === null || matchesQuery(row.search_text)),
  );
  matching.sort(compareRows);
  const total = matching.length;
  const totalPages = Math.ceil(total / pageSize);
  const safePage = Math.min(Math.max(1, Math.trunc(page) || 1), Math.max(1, totalPages));
  const offset = (safePage - 1) * pageSize;
  return {
    items: matching.slice(offset, offset + pageSize),
    total,
    totalPages,
    page: safePage,
  };
}
